import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Card from '../common/Card';
import { useApi } from '../../hooks/useApi';
import { getStockHealth } from '../../api/client';
import { getStockLevelColor } from '../common/StatusIndicator';
import { BarChart3, Filter } from 'lucide-react';

const CATEGORIES = [
  'ALL', 'AIRCRAFT', 'ATTACHMENTS', 'BOATS', 'BUILDINGSUPPLIES', 'CLOTHING',
  'CLOTHING_ACCESSOIRES', 'CONSUMABLES', 'COMPONENTS', 'EVENT', 'EXCHANGE',
  'FISHING', 'MEDICALS', 'SPECIAL', 'VEHICLE_PARTS', 'VEHICLES', 'WEAPONS',
  'ARTEFACTS', 'MUTANTS',
];

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className="glass-card p-3 text-xs">
      <p className="text-text-primary font-medium mb-1">{data.name}</p>
      <p className="text-text-secondary font-mono">
        Stock: <span className="text-accent-green">{data.stockPercent?.toFixed(1)}%</span>
      </p>
      <p className="text-text-secondary font-mono">
        Items: <span className="text-text-primary">{data.totalItems?.toLocaleString()}</span>
      </p>
    </div>
  );
}

export default function StockOverview() {
  const navigate = useNavigate();
  const [category, setCategory] = useState('ALL');
  const { data: health, loading } = useApi(getStockHealth);

  const chartData = useMemo(() => {
    if (!health) return [];

    const zones = Array.isArray(health) ? health : health.zones || [];
    return zones.map((z) => ({
      name: z.zone || z.name,
      stockPercent: z.stockPercent ?? z.healthPercent ?? z.stock_percent ?? 50,
      totalItems: z.totalItems ?? z.total_items ?? 0,
    }));
  }, [health, category]);

  return (
    <Card title="Stock Levels by Zone" icon={BarChart3} className="h-full">
      {/* Category Filter */}
      <div className="flex items-center gap-2 mb-4">
        <Filter className="w-3.5 h-3.5 text-text-muted" />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="bg-terminal-bg border border-terminal-border rounded px-2 py-1 text-xs text-text-secondary font-mono focus:outline-none focus:border-accent-green/50"
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="skeleton h-8 rounded" />
          ))}
        </div>
      ) : chartData.length === 0 ? (
        <div className="flex items-center justify-center h-48 text-text-muted text-sm">
          No stock data available
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
          >
            <XAxis
              type="number"
              domain={[0, 100]}
              tick={{ fill: '#475569', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              axisLine={{ stroke: '#1e293b' }}
              tickLine={false}
              tickFormatter={(v) => `${v}%`}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={120}
              tick={{ fill: '#94a3b8', fontSize: 11, fontFamily: 'JetBrains Mono' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(30, 41, 59, 0.5)' }} />
            <Bar
              dataKey="stockPercent"
              radius={[0, 4, 4, 0]}
              cursor="pointer"
              onClick={(entry) => navigate(`/zones/${encodeURIComponent(entry.name)}`)}
            >
              {chartData.map((entry, idx) => (
                <Cell
                  key={idx}
                  fill={getStockLevelColor(entry.stockPercent)}
                  fillOpacity={0.8}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}
